import { useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Checkbox from '@/design_system/Checkbox';
import Menu, { MenuItem } from '@/design_system/Menu/Menu';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconArchive from '@/icons/Archive.svg';
import IconChevron from '@/icons/Chevron.svg';
import IconCross from '@/icons/Cross.svg';
import IconNotification from '@/icons/Notification.svg';
import IconPerson from '@/icons/User.svg';
import { RequestWithRelationsFromRequestsList } from '@/models/request';
import {
  BulkArchiveDialog,
  BulkAssignDialog,
  BulkConfirmProcessingReceptionDialog,
  BulkFollowDialog,
  BulkTaskDialog,
} from '@/routes/Requests/components/BulkDialogs';
import { Tab } from '@/routes/Requests/Filters/FiltersState';
import { RequestSelection } from '@/routes/Requests/hooks/useRequestSelection';
import { useCurrentSession } from '@/services/auth';
import { createBEMClasses } from '@/utils/classname';

import {
  type BulkableTaskType,
  getBulkEligibility,
  isBulkableTaskType,
} from './getBulkEligibility';
import { getBulkTaskLabel } from './getBulkTaskLabel';

import './BulkActionBar.css';

const { block, element } = createBEMClasses('bulk-action-bar');

type OpenDialog = 'assign' | 'archive' | 'follow' | 'confirm-processing-reception' | 'task' | null;

export const BulkActionBar = ({
  requests,
  selection,
  tab,
}: {
  requests: RequestWithRelationsFromRequestsList[];
  selection: RequestSelection;
  tab: Tab;
}) => {
  const { t, i18n } = useLingui();
  const { currentSession } = useCurrentSession();
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const [activeTaskType, setActiveTaskType] = useState<BulkableTaskType | null>(null);

  const selectedRequests = useMemo(
    () => requests.filter((request) => selection.selectedIds.has(request.id)),
    [requests, selection.selectedIds]
  );

  const eligibility = useMemo(
    () => getBulkEligibility(selectedRequests, currentSession),
    [currentSession, selectedRequests]
  );

  if (selection.selectedIds.size === 0) {
    return null;
  }

  const selectedCount = selection.selectedIds.size;
  const canAssign = eligibility.assign.length > 0;
  const canArchive = eligibility.archive.length > 0;
  const presentTaskTypes = eligibility.presentTaskTypes;
  const singleTaskType = presentTaskTypes.length === 1 ? presentTaskTypes[0] : null;
  const canConfirmReception =
    tab === 'waiting' && eligibility.confirmProcessingReception.articleIds.length > 0;
  const withCount = (label: string, count: number): string => `${label} (${count})`;
  const followLabel = eligibility.follow.shouldUnfollow
    ? t({ id: 'requests.bulk.unsubscribe.confirm', message: 'Unsubscribe' })
    : t({ id: 'requests.bulk.subscribe.confirm', message: 'Subscribe' });
  const clearLabel = t({
    id: 'requests.bulk.bar.clear',
    message: 'Clear selection',
  });
  const completeTaskLabel = t({
    id: 'requests.bulk.bar.complete-task',
    message: 'Complete task',
  });
  const confirmReceptionLabel = withCount(
    t({
      id: 'article.actions.processing.confirm',
      message: 'Confirm reception',
    }),
    eligibility.confirmProcessingReception.articleIds.length
  );

  const closeDialog = () => {
    setOpenDialog(null);
    setActiveTaskType(null);
  };

  const handleSuccess = () => {
    closeDialog();
    selection.clear();
  };

  const openTaskDialog = (taskType: BulkableTaskType) => {
    setActiveTaskType(taskType);
    setOpenDialog('task');
  };

  const completeTaskControl = (compact: boolean) => {
    if (singleTaskType) {
      const taskLabel = withCount(
        getBulkTaskLabel(singleTaskType, i18n),
        eligibility.tasks[singleTaskType].articleIds.length
      );

      return (
        <Button
          className={compact ? element('task') : undefined}
          variant="primary"
          size="medium"
          ariaLabel={taskLabel}
          onPress={() => openTaskDialog(singleTaskType)}
        >
          <span className={compact ? element('task-label') : undefined}>{taskLabel}</span>
        </Button>
      );
    }

    if (presentTaskTypes.length > 1) {
      return (
        <Menu
          placement={compact ? 'top end' : 'top'}
          onAction={(id) => {
            if (isBulkableTaskType(id)) {
              openTaskDialog(id);
            }
          }}
          trigger={
            <Button
              className={compact ? element('task') : undefined}
              variant="primary"
              size="medium"
              ariaLabel={completeTaskLabel}
            >
              <span className={compact ? element('task-label') : undefined}>
                {completeTaskLabel}
              </span>
              <IconChevron down />
            </Button>
          }
        >
          {presentTaskTypes.map((taskType) => (
            <MenuItem id={taskType} key={taskType}>
              {withCount(
                getBulkTaskLabel(taskType, i18n),
                eligibility.tasks[taskType].articleIds.length
              )}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    return null;
  };

  const confirmReceptionButton = (compact: boolean) => {
    if (!canConfirmReception) {
      return null;
    }

    return (
      <Button
        className={compact ? element('task') : undefined}
        variant="primary"
        size="medium"
        ariaLabel={confirmReceptionLabel}
        onPress={() => setOpenDialog('confirm-processing-reception')}
      >
        <span className={compact ? element('task-label') : undefined}>{confirmReceptionLabel}</span>
      </Button>
    );
  };

  return (
    <>
      <div
        className={block()}
        role="toolbar"
        aria-label={t({
          id: 'requests.bulk.bar.label',
          message: 'Bulk actions',
        })}
      >
        <div className={element('toolbar', { desktop: true })}>
          <Stack row gap="0.25rem" alignItems="center" className={element('selection')}>
            <InlineText size="100" weight="medium" color="secondary">
              <Trans id="requests.bulk.bar.selected">{selectedCount} selected</Trans>
            </InlineText>
            <Button
              variant="style-less"
              size="compact"
              iconOnly
              ariaLabel={clearLabel}
              onPress={selection.clear}
            >
              <IconCross />
            </Button>
          </Stack>
          <div className={element('separator')} aria-hidden />
          {completeTaskControl(false)}
          {confirmReceptionButton(false)}
          {canAssign && (
            <Button variant="tertiary" size="medium" onPress={() => setOpenDialog('assign')}>
              <IconPerson />
              <Trans id="requests.bulk.assign.confirm">Assign</Trans>
            </Button>
          )}
          <Button variant="tertiary" size="medium" onPress={() => setOpenDialog('follow')}>
            <IconNotification plain={!eligibility.follow.shouldUnfollow} />
            {followLabel}
          </Button>
          {canArchive && (
            <Button
              variant="tertiary-danger"
              size="medium"
              onPress={() => setOpenDialog('archive')}
            >
              <IconArchive />
              <Trans id="requests.bulk.archive.confirm">Archive</Trans>
            </Button>
          )}
        </div>

        <div className={element('toolbar', { compact: true })}>
          <Stack row gap="0.5rem" alignItems="center" className={element('selection')}>
            <Checkbox
              aria-label={
                selection.isAllSelected
                  ? t({
                      id: 'requests.table.selection.unselect-all',
                      message: 'Unselect all requests on this page',
                    })
                  : t({
                      id: 'requests.table.selection.select-all',
                      message: 'Select all requests on this page',
                    })
              }
              isSelected={selection.isAllSelected}
              isIndeterminate={selection.isIndeterminate}
              onChange={(isSelected) =>
                isSelected ? selection.selectAll(requests.map(({ id }) => id)) : selection.clear()
              }
            />
            <InlineText size="100" weight="medium" color="secondary">
              {selectedCount}
            </InlineText>
          </Stack>
          <div className={element('compact-actions')}>
            <Button
              variant="tertiary"
              size="medium"
              iconOnly
              ariaLabel={followLabel}
              onPress={() => setOpenDialog('follow')}
            >
              <IconNotification plain={!eligibility.follow.shouldUnfollow} />
            </Button>
            {canAssign && (
              <Button
                variant="tertiary"
                size="medium"
                iconOnly
                ariaLabel={t({ id: 'requests.bulk.assign.confirm', message: 'Assign' })}
                onPress={() => setOpenDialog('assign')}
              >
                <IconPerson />
              </Button>
            )}
            {canArchive && (
              <Button
                variant="tertiary-danger"
                size="medium"
                iconOnly
                ariaLabel={t({ id: 'requests.bulk.archive.confirm', message: 'Archive' })}
                onPress={() => setOpenDialog('archive')}
              >
                <IconArchive />
              </Button>
            )}
            {confirmReceptionButton(true)}
            {completeTaskControl(true)}
          </div>
        </div>
      </div>

      <BulkAssignDialog
        showDialog={openDialog === 'assign'}
        setShowDialog={(show) => {
          if (!show) {
            closeDialog();
          }
        }}
        selectedRequests={selectedRequests}
        eligibleRequests={eligibility.assign}
        onSuccess={handleSuccess}
      />
      <BulkArchiveDialog
        showDialog={openDialog === 'archive'}
        setShowDialog={(show) => {
          if (!show) {
            closeDialog();
          }
        }}
        selectedRequests={selectedRequests}
        eligibleRequests={eligibility.archive}
        onSuccess={handleSuccess}
      />
      <BulkFollowDialog
        showDialog={openDialog === 'follow'}
        setShowDialog={(show) => {
          if (!show) {
            closeDialog();
          }
        }}
        selectedRequests={selectedRequests}
        eligibleRequests={eligibility.follow.eligible}
        shouldUnfollow={eligibility.follow.shouldUnfollow}
        onSuccess={handleSuccess}
      />
      <BulkConfirmProcessingReceptionDialog
        showDialog={openDialog === 'confirm-processing-reception'}
        setShowDialog={(show) => {
          if (!show) {
            closeDialog();
          }
        }}
        selectedRequests={selectedRequests}
        scope={eligibility.confirmProcessingReception}
        onSuccess={handleSuccess}
      />
      <BulkTaskDialog
        showDialog={openDialog === 'task'}
        setShowDialog={(show) => {
          if (!show) {
            closeDialog();
          }
        }}
        taskType={activeTaskType}
        scope={activeTaskType ? eligibility.tasks[activeTaskType] : null}
        selectedRequests={selectedRequests}
        onSuccess={handleSuccess}
      />
    </>
  );
};
